import React, { useState, useRef, ChangeEvent, useEffect } from "react";
import {
  Table,
  Button,
  Form,
  InputGroup,
  Modal,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import {
  addUploadRecordFiles,
  deleteUploadRecordFile,
  getDownloadFileForPopUpDetails,
  getVersionList,
  updateUploadRecordFiles,
  uploadChunkRecord,
  uploadChunkRecordFinal,
  uploadPageCount,
} from "../../../../services/UploadRecordsApi";
import Snackbar from "../../../../common/Snackbar";
import { useDispatch, useSelector } from "react-redux";
import axios, { CancelToken } from "axios";
import { RootState } from "../../../../redux/Store";
import { FILE_SIZE, QME_DOCTOR_DOCUMENT_TYPES } from "../../../../common/Constant";
import UploadStatusFloatingIcon from "../../../../common/UploadStatusFloatingIcon";
import {
  setIsCancelFiles,
  setIsUploadFile,
} from "../../../../redux/slice/CaseHeaderSlice";
import { VERSION_HEADER_EDIT } from "../../../Case_Management/Case_Records/actions/Constant";
import { useIsStageReadOnly } from "../../../../utils/caseStageChecker";
import { truncateMiddleString } from "../../../../utils/stringUtils";
import PdfViewerDrawer from "../../../../common/PDFFileViewerDrawer";
import { MiddleTruncatedTextWithTooltip } from "../../../../common/MiddleTruncatedTextWithTooltip";

const MAX_FILE_SIZE_MB = 2048;
const MAX_TOTAL_SIZE_MB = 2048;

const ALLOWED_FILE_TYPES = [
  // Document formats
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "text/plain",
  "text/csv",

  // Image formats
  "image/png",
  "image/jpeg",
  "image/gif",
  "image/tiff",
  "image/heif",
  "image/heic",
  "image/webp",

  // Video formats
  "video/mp4",
  "video/x-msvideo",
  "video/quicktime",
  "video/x-ms-wmv",
  "video/x-matroska",
  "video/webm",
  "video/x-flv",
  "video/3gpp",
  "video/mpeg",
  "video/avchd",

  // Audio formats
  "audio/m4a",
  "audio/flac",
  "audio/mpeg",
  "audio/mp4",
  "audio/wav",
  "audio/x-wav",
  "audio/x-ms-wma",
  "audio/aac",
  "audio/alac",
  "audio/aiff",
  "audio/x-aiff",

  // Archive formats
  "application/zip",
  "application/x-zip-compressed",
];

const ALLOWED_FILE_EXTENSIONS = [
  // Document extensions
  ".pdf",
  ".doc",
  ".docx",
  ".xls",
  ".xlsx",
  ".txt",
  ".csv",

  // Image extensions
  ".png",
  ".jpeg",
  ".jpg",
  ".gif",
  ".tiff",
  ".raw",
  ".heif",
  ".heic",
  ".webp",

  // Video extensions
  ".mp4",
  ".avi",
  ".mov",
  ".wmv",
  ".mkv",
  ".webm",
  ".flv",
  ".3gp",
  ".mpeg",
  ".avchd",

  // Audio extensions
  ".m4a",
  ".flac",
  ".mp3",
  ".mp4",
  ".wav",
  ".wma",
  ".aac",
  ".alac",
  ".aiff",

  // Archive extensions
  ".zip",
];

function isAllowedFileType(file: File): boolean {
  const mimeTypeAllowed = ALLOWED_FILE_TYPES.includes(file.type);
  const extensionAllowed = ALLOWED_FILE_EXTENSIONS.some((ext) =>
    file.name.toLowerCase().endsWith(ext)
  );
  return mimeTypeAllowed || extensionAllowed;
}

interface FileData {
  id?: any;
  file?: File;
  fileName: string;
  description: string;
  noofPages?: any;
  isEditing: boolean;
  cloudStoragePath?: string;
  parentId?: number | string | null;
  caseRecordDetailsID?: number | null;
  fileVersionTargetId?: any;
}

interface EditableFileTableProps {
  caseInfoId: any;
  comments?: any;
  initialData?: FileData[];
  onAdd?: (fileData: FileData) => void;
  onEdit?: (id: string, fileData: FileData) => void;
  onDelete?: (id: string) => void;
  onFileIdsUpdate?: (
    ids: string[],
    isSaveDisabled?: boolean,
    fileActions?: any
  ) => void;
  result: any;
  isCancel: boolean;
  documentType: string | null | number;
  isRowActionTriggered?: boolean;
  selectedRowData?: any;
  recordActiveTab?: string;
  activeTab?: string;
  isAllCancel?: boolean;
  setIsAllCancel?: any;
  isRestirctEdit?: any;
  isEditorOrBillingRole?: any;
  existsDocType?: any;
  onViwerOpen?: (value: string) => void;
  documentTypeName?: any;
}

interface UploadStatus {
  fileName?: string;
  progress: number;
  isUploading: boolean;
  cancel?: () => void;
}

const finalDocumentTypes = [
  QME_DOCTOR_DOCUMENT_TYPES.SUPPLEMENTAL_REPORT,
  QME_DOCTOR_DOCUMENT_TYPES.INITIAL_EVALUATION_REPORT,
  QME_DOCTOR_DOCUMENT_TYPES.RE_EVALUATION_REPORT,
  QME_DOCTOR_DOCUMENT_TYPES.SIBTF_REPORT,
  QME_DOCTOR_DOCUMENT_TYPES.QME_FINAL_REPORT,
  "Injured Worker History Report",
  "Record Review Summary",
  "Transcription",
];

const HistorianRecordEditableTable: React.FC<EditableFileTableProps> = ({
  caseInfoId,
  comments,
  initialData = [],
  onAdd,
  onEdit,
  onDelete,
  onFileIdsUpdate,
  result,
  isCancel,
  documentType,
  isRowActionTriggered,
  selectedRowData,
  recordActiveTab,
  activeTab,
  isAllCancel,
  setIsAllCancel,
  isRestirctEdit,
  isEditorOrBillingRole,
  existsDocType,
  onViwerOpen,
  documentTypeName,
}) => {
  const dispatch = useDispatch();
  const { isCancelFiles } = useSelector((state: RootState) => state.caseHeader);
  const [files, setFiles] = useState<FileData[]>(initialData);
  const [originalData, setOriginalData] = useState<{ [key: string]: FileData }>(
    {}
  );
  const [newFile, setNewFile] = useState<any>({
    id: "",
    files: [],
    fileName: "",
    description: "",
    noofPages: "",
    cloudStoragePath: "",
    isEditing: true,
  });
  const [newVersionFile, setNewVersionFile] = useState<any>({
    id: "",
    fileName: "",
    description: "",
    isEditing: true,
  });
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingNew, setLoadingNew] = useState<boolean>(false);
  const [isSaveBtnDisable, setIsSaveBtnDisable] = useState<boolean>(false);
  const [isSaveBtnDisableForRow, setIsSaveBtnDisableForRow] =
    useState<boolean>(false);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [showModalNew, setShowModalNew] = useState(false);
  const [fileToCancel, setFileToCancel] = useState<string>("");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const fileNewVersionInputRef = useRef<HTMLInputElement>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [error, setError] = useState("");
  const editFileInputRefs = useRef<{ [key: string]: HTMLInputElement | null }>(
    {}
  );
  const [fileToDelete, setFileToDelete] = useState<{
    id: number;
    fileName: string;
    fileDetails: any;
  } | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showNewVersionModal, setShowNewVersionModal] = useState(false);
  const prevDocumentTypeRef = useRef<string | number | null>(null);
  const [fileVersionTargetId, setFileVersionTargetId] = useState<number | null>(
    null
  );
  const [caseRecordDetailsIDParent, setCaseRecordDetailsIDParent] = useState<
    number | null
  >(null);
  const [fileNameParent, setFileNameParent] = useState<string>("");
  const [fileActions, setFileActions] = useState<
    {
      provisionid: number | null;
      caserecorddetailsid: number | null;
      action:
        | "NewFile"
        | "AddRevision"
        | "UpdateRevision"
        | "DeleteRevision"
        | "DeleteFile";
    }[]
  >([]);
  const [parentCaseRecordDetailsId, setParentCaseRecordDetailsId] = useState<
    number | null
  >(null);
  const [tooltipKey, setTooltipKey] = useState(0);

  const addFileAction = (
    provisionid: number | null,
    caserecorddetailsid: number | null,
    action:
      | "NewFile"
      | "AddRevision"
      | "UpdateRevision"
      | "DeleteRevision"
      | "DeleteFile"
  ) => {
    setFileActions((prev) => [
      ...prev,
      { provisionid, caserecorddetailsid, action },
    ]);
  };
  const [uploadStatuses, setUploadStatuses] = useState<
    Record<string, UploadStatus>
  >({});
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [rowChildren, setRowChildren] = useState<Record<string, any[]>>({});
  const [showMessage, setShowMessage] = useState<boolean>(false);
  const [totalPagesCount, setTotalPagesCount] = useState<number | null>(null);
  const [isCancelRequest, setIsCancelRequest] = useState<boolean>(false);
  const finalDocument = finalDocumentTypes.includes(documentTypeName);

  const isVersionUploadAllowed = (fileName: string = ""): boolean => {
    const allowedExtensions = [
      ".pdf",
      ".doc",
      ".docx",
      ".xls",
      ".xlsx",
      ".csv",
    ];
    return allowedExtensions.some((ext) =>
      fileName.toLowerCase().endsWith(ext)
    );
  };

  const getTotalSize = () =>
    files.reduce((acc, file) => acc + (file.file?.size || 0), 0);
  const MAX_FILE_NAME_LENGTH = 100;
  const MAX_DESCRIPTION_LENGTH = 300;
  const ALPHANUMERIC_REGEX = /^[a-zA-Z0-9 _.,\-()]+$/;
  const ALPHANUMERIC_REGEXDESC =
    /^[a-zA-Z0-9\s!@#$%^&*()_+\-=\[\]{};':"\\|,.<>/?`~]+$/;

  const showError = (message: string) => {
    setSnackbarMessage(message);
    setError("error");
    setSnackbarOpen(true);
  };

  const allowedNewVersionExtensions = [
    ".pdf",
    ".doc",
    ".docx",
    ".xls",
    ".xlsx",
    ".csv",
  ];
  const isAllowedNewVerFileType = (file: File): boolean => {
    const fileName = file.name.toLowerCase();
    return allowedNewVersionExtensions.some((ext) => fileName.endsWith(ext));
  };

  const validateFile = (file: File, isNewVersion: boolean): boolean => {
    const errors: string[] = [];

    if (file.size === 0) {
      errors.push("The uploaded file is empty. Please select a valid file.");
    }

    if (isNewVersion) {
      if (!isAllowedNewVerFileType(file)) {
        errors.push(
          "Invalid file type. Only PDF, Word, and Excel documents are allowed."
        );
      }
    } else {
      if (!isAllowedFileType(file)) {
        errors.push(
          "Invalid file type. Please upload a PDF, Word document, image, spreadsheet, video, audio, or text file."
        );
      }
    }

    if (errors?.length > 0) {
      setShowNewVersionModal(false);
      showError(errors.join("\n"));
      return false;
    }

    return true;
  };

  const validateFileName_Description = (
    fileName: string,
    description?: string,
    existingFiles?: any,
    currentId?: string,
    isNewVersion: boolean = false,
    fileNameParent?: string,
    hasVersionParent: boolean = false
  ): boolean => {
    const errors: string[] = [];

    if (!ALPHANUMERIC_REGEX.test(fileName)) {
      errors.push(
        "File name contains invalid characters. Please rename the file and try again."
      );
    }

    if (fileName?.length > MAX_FILE_NAME_LENGTH) {
      errors.push(
        `File name is too long. Maximum allowed length is ${MAX_FILE_NAME_LENGTH} characters.`
      );
    }

    if (errors?.length > 0) {
      showError(errors.join("\n"));
      return false;
    }

    return true;
  };

  // Document type changed - reset all file fields
  useEffect(() => {
    if (
      prevDocumentTypeRef.current !== null &&
      documentType !== prevDocumentTypeRef.current
    ) {
      setFiles([]);
      setNewFile({
        id: "",
        files: [],
        fileName: "",
        description: "",
        isEditing: true,
      });
      if (fileInputRef.current) fileInputRef.current.value = "";
      handleCancelALLUpload();
      onFileIdsUpdate?.([]);
    }

    prevDocumentTypeRef.current = documentType;
  }, [documentType]);

  useEffect(() => {
    setExpandedRows(new Set());
    setRowChildren({});
    if (Array.isArray(result)) {
      const allFiles = result.flatMap(
        (item) =>
          item.addressesJson?.map((file: any) => ({
            id: file.provisionalAttachmentID,
            caseRecordDetailsID: file.caseRecordDetailsID,
            fileName: file.fileName,
            noofPages: file.noofPages,
            description: file.description || "",
            file: file.cloudLocation,
            cloudStoragePath: file.cloudLocation,
            isEditing: false,
          })) || []
      );

      const totalPages = allFiles.reduce((sum, file) => {
        const pages = Number(file.noofPages);
        return sum + (isNaN(pages) ? 0 : pages);
      }, 0);
      setFiles(allFiles);
      setFileActions([]);
      setTotalPagesCount(totalPages || null);
    } else {
      setFiles([]);
      setFileActions([]);
    }
  }, [result]);

  // Reset the new file data
  useEffect(() => {
    if (isCancel) {
      setNewFile({
        id: "",
        files: [],
        fileName: "",
        description: "",
        isEditing: true,
      });

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      setFileActions([]);
      handleCancelALLUpload();
    }
  }, [isCancel]);

  // Rename if name already exists
  const getRenamedFileIfDuplicate = (
    file: File,
    existingFileNames: string[]
  ): File => {
    let originalName = file.name;
    let newName = originalName;
    const parts = originalName.split(".");
    const ext = parts.length > 1 ? parts.pop()! : "";
    const base = parts.join(".");
    let counter = 1;

    while (existingFileNames.includes(newName.toLowerCase())) {
      newName = `${base} (${counter}).${ext}`;
      counter++;
    }

    return new File([file], newName, { type: file.type });
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (finalDocumentDisable || existsDocType) return;
    if (e.target.files && e.target.files.length > 0) {
      const selectedFiles = Array.from(e.target.files);
      e.target.value = "";

      const validFiles = selectedFiles.every((file) =>
        validateFile(file, false)
      );
      if (!validFiles) return;

      // Existing file names (case-insensitive, from both new and old)
      const existingFileNames = files
        .map((item) => item.fileName?.toLowerCase())
        .filter((name): name is string => !!name);

      // Generate renamed File objects with unique names
      const renamedFiles = selectedFiles.map((file) => {
        const uniqueFile = getRenamedFileIfDuplicate(file, existingFileNames);
        existingFileNames.push(uniqueFile.name.toLowerCase());
        return uniqueFile;
      });

      setIsSaveBtnDisableForRow(true);
      setNewFile({ ...newFile, files: renamedFiles });
    }
  };

  useEffect(() => {
    if (newFile.files?.length && !loading) {
      handleAddFile();
    }
  }, [newFile?.files]);

  const getExistingFileNames = (
    files: FileData[],
    excludeId?: string
  ): string[] => {
    return files
      .filter((f) => !excludeId || f.id !== excludeId)
      .map((f) => f.fileName?.toLowerCase())
      .filter((name): name is string => !!name);
  };

  const handleNewVersionFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files?.length > 0) {
      setIsSaveBtnDisableForRow(true);
      const file = e.target.files[0];
      e.target.value = "";
      if (!validateFile(file, true)) return;
      // Collect existing file names from current `files`
      const existingFileNames = files
        ?.map((item) => item.fileName?.toLowerCase())
        .filter((name): name is string => !!name);

      // Rename the file if it already exists
      const renamedFile = getRenamedFileIfDuplicate(file, existingFileNames);
      setNewVersionFile({
        ...newVersionFile,
        file: renamedFile,
        fileName: renamedFile.name,
      });
    }
  };

  const handleEditFileChange = (
    id: string,
    e: ChangeEvent<HTMLInputElement>
  ) => {
    if (e.target.files && e.target.files?.length > 0) {
      let file = e.target.files[0];
      e.target.value = "";
      if (!validateFile(file, false)) return;
      const existingFileNames = getExistingFileNames(files, id);
      file = getRenamedFileIfDuplicate(file, existingFileNames);
      setFiles(
        files.map((item) =>
          item.id === id ? { ...item, file, fileName: file.name } : item
        )
      );
    }
  };

  const updateFileIdsInParent = (updatedFiles: FileData[]) => {
    const filteredActions = fileActions.filter((action, index, self) => {
      const { provisionid, action: actionType } = action;
      const hasConflict = self.some(
        (other, otherIndex) =>
          otherIndex !== index &&
          other.provisionid === provisionid &&
          ((["AddRevision", "NewFile"].includes(actionType) &&
            ["DeleteRevision", "DeleteFile"].includes(other.action)) ||
            (["DeleteRevision", "DeleteFile"].includes(actionType) &&
              ["AddRevision", "NewFile"].includes(other.action)))
      );

      return !hasConflict;
    });

    const normalisedActions = filteredActions.map((a) =>
      ["DeleteRevision", "DeleteFile"].includes(a.action)
        ? { ...a, provisionid: null }
        : a
    );

    const fileIds = updatedFiles.map((file) => file.id).filter(Boolean);
    onFileIdsUpdate?.(fileIds, isSaveBtnDisableForRow, normalisedActions);
  };

  React.useEffect(() => {
    updateFileIdsInParent(files);
  }, [files, isSaveBtnDisableForRow]);

  const CHUNK_SIZE = 8 * 1024 * 1024;

  const uploadFileInChunks = async (
    file: File,
    caseInfoId: string,
    description: string,
    additionalInfo: string,
    progress: (uploadedBytes: number) => void,
    cancelToken: CancelToken,
    onPageCountUpdate?: (fileId: string, pageCount: number) => void
  ): Promise<any> => {
    const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
    const abortControllers: AbortController[] = [];

    try {
      // Upload each chunk
      for (let i = 0; i < totalChunks; i++) {
        if (cancelToken.reason) throw cancelToken.reason;

        const start = i * CHUNK_SIZE;
        const end = Math.min(file.size, start + CHUNK_SIZE);
        const chunk = file.slice(start, end);

        const formData = new FormData();
        formData.append("File", chunk);
        formData.append("FileName", file.name);
        formData.append("ChunkNumber", (i + 1).toString());

        // Create new AbortController for each request
        const abortController = new AbortController();
        abortControllers.push(abortController);

        try {
          await uploadChunkRecord(formData, {
            cancelToken,
            signal: abortController.signal,
          });

          // const percent = Math.round(((i + 1) / totalChunks) * 99);
          const percent = Math.min(
            99,
            Math.round(((i + 1) / totalChunks) * 100)
          );
          progress(percent);
        } finally {
          // Remove the controller after request completes
          abortControllers.pop();
        }
      }

      // Check cancellation before finalizing
      if (cancelToken.reason) throw cancelToken.reason;

      // Finalize upload
      const finalizePayload = {
        fileName: file.name,
        totalChunks: totalChunks,
        originalFileName: file.name,
        caseInfoId: Number(caseInfoId),
        description: description,
        additionalFileInfo: additionalInfo,
      };

      const result = await uploadChunkRecordFinal(finalizePayload, cancelToken);
      if (result?.isSuccess && result?.result) {
        const provisionalId: any = result?.result;
        uploadPageCountAsync(file, provisionalId?.id, onPageCountUpdate);
        progress(100);
        return result;
      } else {
        throw new Error("Finalization failed - no ID returned");
      }
    } catch (error) {
      // Abort all ongoing requests
      abortControllers.forEach((controller) => controller.abort());

      if (axios.isCancel(error)) {
        console.error("Upload cancelled:", error.message);
        throw error;
      }
      throw error;
    }
  };

   const uploadPageCountAsync = async (
    file: File,
    provisionalAttachmentId: string,
    onPageCountUpdate?: (fileId: string, pageCount: number) => void
  ) => {
    try {
      const updateFormData = new FormData();
      updateFormData.append("File", file);
      updateFormData.append("ProvisionalAttachmentId", provisionalAttachmentId);
      const updatePageCount = await uploadPageCount(updateFormData);
      
      if (updatePageCount?.isSuccess && updatePageCount?.result?.pageCount && onPageCountUpdate) {
        onPageCountUpdate(provisionalAttachmentId, updatePageCount.result.pageCount);
      }
    } catch (error) {
      console.error("Error updating page count:", error);
    }
  };

  const handleAddFile = async () => {
    if (!newFile.files?.length) return;

    for (const file of newFile.files) {
      const isValid = validateFileName_Description(
        file.name,
        newFile.description,
        files
      );

      if (!isValid) {
        setSnackbarOpen(true);
        setError("error");
        return;
      }
    }
    // Prevent uploading same file(s) if already uploading
    const uploadingFileNames = Object.values(uploadStatuses)
      .filter((status) => status.isUploading)
      .flatMap((status) => status.fileName || []);

    const duplicateFiles = newFile.files.filter((file: any) =>
      uploadingFileNames.includes(file.name)
    );

    if (duplicateFiles.length > 0) {
      const message =
        duplicateFiles.length === 1
          ? `The file "${duplicateFiles[0].name}" is already being uploaded.`
          : `The files ${duplicateFiles
              .map((f: any) => `"${f.name}"`)
              .join(", ")} are already being uploaded.`;
      setSnackbarMessage(message);
      setError("error");
      setSnackbarOpen(true);
      return;
    }

    const uploadedFiles: FileData[] = [];

    // Size check (2 GB)
    const largeFile = newFile?.files?.find(
      (file: File) => file.size > FILE_SIZE.TWO_GB
    );
    if (largeFile) {
      setSnackbarMessage(
        `File "${largeFile.name}" exceeds the maximum allowed size of 2 GB.`
      );
      setError("error");
      setSnackbarOpen(true);
      return;
    }

    setLoading(true);
    setUploadProgress(0);
    setIsCancelRequest(false);

    // Store cancel tokens per file
    const fileCancelTokens: Record<number, any> = {};

    // Create upload status entries for all files before uploading starts
    const fileIds: number[] = [];
    newFile.files.forEach((file: any) => {
      const uniqueFileId = Date.now() + Math.random();
      fileIds.push(uniqueFileId);

      const fileCancelTokenSource = axios.CancelToken.source();
      fileCancelTokens[uniqueFileId] = fileCancelTokenSource;

      setUploadStatuses((prev) => ({
        ...prev,
        [uniqueFileId]: {
          fileName: file.name,
          progress: 0,
          isUploading: true,
          cancel: () => {
            fileCancelTokenSource.cancel("Upload cancelled by user");
          },
        },
      }));
    });

    try {
      // Sequential upload: one file at a time
      for (let i = 0; i < newFile.files.length; i++) {
        const fileObj = newFile.files[i];
        const uniqueFileId = fileIds[i];
        const fileCancelTokenSource = fileCancelTokens[uniqueFileId];

        try {
          const result = await uploadFileInChunks(
            fileObj,
            caseInfoId,
            newFile.description,
            comments,
            (progress: number) => {
              setUploadStatuses((prev) => ({
                ...prev,
                [uniqueFileId]: {
                  ...prev[uniqueFileId],
                  progress,
                },
              }));
            },
            fileCancelTokenSource.token,
            (fileId: string, pageCount: number) => {
              setFiles((prevFiles) => 
                prevFiles.map((file) => 
                  file.id === fileId 
                    ? { ...file, noofPages: pageCount }
                    : file
                )
              );
            }
          );

          if (result?.isSuccess && result.result?.id) {
            const res = result.result;
            const fileData: FileData = {
              fileName: fileObj.name,
              file: fileObj,
              description: newFile.description,
              id: res.id,
              isEditing: false,
              noofPages: null,
              cloudStoragePath: res.cloudStoragePath,
            };
            setFiles((prevFiles) => [fileData, ...prevFiles]);
            uploadedFiles.push(fileData);
            addFileAction(res.id, null, "NewFile");
            onAdd?.(fileData);
          } else {
            console.error(`File "${fileObj.name}" upload failed.`);
          }
        } catch (error) {
          if (axios.isCancel(error)) {
            console.error(`Upload cancelled for ${fileObj.name}`);
          } else {
            console.error("Error adding file:", error);
          }
        } finally {
          // Remove this file from statuses after it finishes/cancels
          setUploadStatuses((prev) => {
            const newStatuses = { ...prev };
            delete newStatuses[uniqueFileId];
            return newStatuses;
          });
          delete fileCancelTokens[uniqueFileId];
        }
      }
    } catch (error) {
      console.error("Error in upload process:", error);
    } finally {
      if (uploadedFiles?.length > 0) {
        setSnackbarMessage("Record Uploaded Successfully");
        setError("success");
        setSnackbarOpen(true);
        setIsSaveBtnDisable(true);
        setIsSaveBtnDisableForRow(false);
      }

      setNewFile({ files: [], description: "" });
      if (fileInputRef.current) fileInputRef.current.value = "";
      setUploadProgress(0);
      setLoading(false);
      setIsCancelRequest(false);
    }
  };

  const hasVersions = (fileId: number | string): boolean => {
    return files.some((file) => file.parentId === fileId);
  };

  const handleAddNewVersionFile = async () => {
    if (!newVersionFile.file) return;
    // Prevent uploading same file(s) if upload already in progress
    const uploadingFileNames = Object.values(uploadStatuses)
      .filter((status) => status.isUploading)
      .flatMap((status) => status.fileName || []);

    const isDuplicate = uploadingFileNames.includes(newVersionFile.file.name);
    if (isDuplicate) {
      setShowNewVersionModal(false);
      setSnackbarMessage(
        `The file "${newVersionFile.file.name}" is already being uploaded.`
      );
      setError("error");
      setSnackbarOpen(true);
      return;
    }
    // Check file size limit (2GB)
    const largeFile = newVersionFile?.files?.find(
      (file: File) => file.size > FILE_SIZE.TWO_GB
    );
    if (largeFile) {
      setSnackbarMessage(
        `File "${largeFile.name}" exceeds the maximum allowed size of 2 GB.`
      );
      setError("error");
      setSnackbarOpen(true);
      return;
    }
    if (
      !validateFileName_Description(
        newVersionFile.fileName,
        newVersionFile.description,
        files,
        "",
        true,
        fileNameParent
      )
    )
      return;
    const formData = new FormData();
    formData.append("Files", newVersionFile.file);
    formData.append("CaseInfoId", caseInfoId);
    formData.append("FileName", newVersionFile.fileName);
    formData.append("Description", newVersionFile.description);
    formData.append("AdditionalFileInfo", comments);
    const fileId = Date.now() + Math.random();
    try {
      setShowNewVersionModal(false);
      setLoading(true);
      setUploadProgress(0);

      // Track upload
      const cancelTokenSource = axios.CancelToken.source();
      setUploadStatuses((prev) => ({
        ...prev,
        [fileId]: {
          fileName: newVersionFile.fileName,
          progress: 0,
          isUploading: true,
          cancel: () => cancelTokenSource.cancel("Upload cancelled"),
        },
      }));

      const result = await uploadFileInChunks(
        newVersionFile.file,
        caseInfoId,
        newVersionFile.description,
        comments,
        (progress: number) => {
          setUploadStatuses((prev) => ({
            ...prev,
            [fileId]: {
              ...prev[fileId],
              progress,
            },
          }));
        },
        cancelTokenSource.token,
        (fileId: string, pageCount: number) => {
          setFiles((prevFiles) => 
            prevFiles.map((file) => 
              file.id === fileId 
                ? { ...file, noofPages: pageCount }
                : file
            )
          );
        }
      );

      if (result?.isSuccess && result.result?.id) {
        setSnackbarMessage("Record Uploaded Successfully");
        setError("success");
        setSnackbarOpen(true);
        setIsSaveBtnDisable(true);
        setIsSaveBtnDisableForRow(false);
      }

      const updatedPageCount =
        result?.result?.noofPages ?? newFile.noofPages ?? 0;

      const fileLocation =
        result?.result?.cloudStoragePath ?? newFile.cloudStoragePath ?? 0;

      const fileData = {
        ...newVersionFile,
        id: result?.result?.id,
        isEditing: false,
        parentId: fileVersionTargetId,
        noofPages: null,
        cloudStoragePath: fileLocation,
      };
      addFileAction(
        result?.result?.id,
        parentCaseRecordDetailsId
          ? parentCaseRecordDetailsId
          : caseRecordDetailsIDParent,
        "AddRevision"
      );

      setFiles((prevFiles) => {
        const index = prevFiles.findIndex((f) => f.id === fileVersionTargetId);
        if (index === -1) return prevFiles;
        const updated = [...prevFiles];
        updated[index] = {
          ...fileData,
          fileVersionTargetId: fileVersionTargetId,
        };
        return updated;
      });

      onAdd?.(fileData);
      setNewVersionFile({
        id: "",
        fileName: "",
        description: "",
        isEditing: true,
      });

      if (fileNewVersionInputRef.current)
        fileNewVersionInputRef.current.value = "";
      setFileVersionTargetId(null);
      setShowNewVersionModal(false);
    } catch (error) {
      console.error("Error adding file:", error);
      setNewVersionFile({
        id: "",
        fileName: "",
        description: "",
        isEditing: true,
      });

      if (fileNewVersionInputRef.current)
        fileNewVersionInputRef.current.value = "";
      setUploadStatuses((prev) => ({
        ...prev,
        [fileId]: {
          ...prev[fileId],
          progress: 0,
          isUploading: false,
        },
      }));
    } finally {
      // Clean up status
      setUploadStatuses((prev) => {
        const newStatuses = { ...prev };
        delete newStatuses[fileId];
        return newStatuses;
      });
      setUploadProgress(0);
      setLoading(false);
    }
  };

  const handleEdit = (id: string) => {
    setIsSaveBtnDisableForRow(true);
    const fileToEdit = files.find((file) => file.id === id);
    if (fileToEdit) {
      setOriginalData((prev) => ({
        ...prev,
        [id]: { ...fileToEdit, isEditing: true },
      }));
    }

    setFiles(
      files.map((item) =>
        item.id === id ? { ...item, isEditing: true } : item
      )
    );
  };

  const handleSaveEdit = async (
    id: string,
    fileDetails: any,
    hasVersion: boolean,
    isParent: boolean
  ) => {
    const fileToUpdate = files.find((file) => file.id === id);
    const originalFile = originalData[id];
    const hasVersionParent = hasVersion || isParent;

    if (!fileToUpdate) return;

    // Prevent uploading same file(s) if upload already in progress
    const uploadingFileNames = Object.values(uploadStatuses)
      .filter((status) => status.isUploading)
      .flatMap((status) => status.fileName || []);

    const isDuplicate = uploadingFileNames.includes(fileToUpdate.fileName);
    if (isDuplicate) {
      setShowNewVersionModal(false);
      setSnackbarMessage(
        `The file "${fileToUpdate.fileName}" is already being uploaded.`
      );
      setError("error");
      setSnackbarOpen(true);
      return;
    }

    if (
      !validateFileName_Description(
        fileToUpdate.fileName,
        fileToUpdate.description,
        files,
        fileToUpdate.id,
        false,
        "",
        hasVersionParent
      )
    )
      return;

    const formData = new FormData();
    formData.append("ProvisionalAttachmentId", id);
    formData.append("CaseInfoId", caseInfoId);
    formData.append("Description", fileToUpdate.description);
    formData.append("AdditionalFileInfo", comments);

    const isFileChanged =
      fileToUpdate.file instanceof File &&
      fileToUpdate.file.name !== originalFile?.fileName;

    let fileId: string | number = "";
    let cancelTokenSource: any;

    try {
      setLoading(true);
      setUploadProgress(0);

      if (isFileChanged && fileToUpdate.file) {
        fileId = Date.now() + Math.random();
        cancelTokenSource = axios.CancelToken.source();

        formData.append("file", fileToUpdate.file);
        formData.append("FileName", fileToUpdate.fileName);

        setUploadStatuses((prev) => ({
          ...prev,
          [fileId]: {
            fileName: fileToUpdate.fileName,
            progress: 0,
            isUploading: true,
            cancel: () => cancelTokenSource.cancel("Upload cancelled"),
          },
        }));

        const resultUpdate = await updateUploadRecordFiles(
          formData,
          (progress: number) => {
            setUploadStatuses((prev) => ({
              ...prev,
              [fileId]: {
                ...prev[fileId],
                progress,
              },
            }));
          },
          cancelTokenSource.token
        );

        const updatedPageCount =
          resultUpdate?.result?.noofPages ?? fileToUpdate.noofPages ?? 0;

        handlePostUpdate(
          resultUpdate,
          id,
          fileToUpdate,
          fileDetails,
          updatedPageCount
        );
      } else {
        formData.append("file", "");
        formData.append("FileName", "");

        const resultUpdate = await updateUploadRecordFiles(formData, () => {});

        const updatedPageCount =
          resultUpdate?.result?.noofPages ?? fileToUpdate.noofPages ?? 0;

        handlePostUpdate(
          resultUpdate,
          id,
          fileToUpdate,
          fileDetails,
          updatedPageCount
        );
      }
    } catch (error) {
      console.error("Error updating file:", error);
      if (fileId) {
        setUploadStatuses((prev) => ({
          ...prev,
          [fileId]: {
            ...prev[fileId],
            isUploading: false,
            progress: 0,
          },
        }));
      }
    } finally {
      if (fileId) {
        setUploadStatuses((prev) => {
          const newStatuses = { ...prev };
          delete newStatuses[fileId];
          return newStatuses;
        });
      }
      setUploadProgress(0);
      setLoading(false);
    }
  };

  const handlePostUpdate = (
    resultUpdate: any,
    id: string,
    fileToUpdate: any,
    fileDetails: any,
    updatedPageCount: number
  ) => {
    if (resultUpdate?.isSuccess) {
      setSnackbarMessage("Record Uploaded Successfully");
      setError("success");
      setSnackbarOpen(true);
      setIsSaveBtnDisable(true);
      setIsSaveBtnDisableForRow(false);

      addFileAction(
        Number(id),
        Number(fileDetails?.caseRecordDetailsID),
        "UpdateRevision"
      );

      const updatedFile = {
        ...fileToUpdate,
        isEditing: false,
        noofPages: updatedPageCount,
      };
      setFiles(files.map((item) => (item.id === id ? updatedFile : item)));
      onEdit?.(id, { ...fileToUpdate, isEditing: false });
    }
  };

  const handleCancelEdit = (id: string) => {
    if (originalData[id]) {
      setFiles(
        files.map((item) =>
          item.id === id ? { ...originalData[id], isEditing: false } : item
        )
      );
    } else {
      setFiles(
        files.map((item) =>
          item.id === id ? { ...item, isEditing: false } : item
        )
      );
    }
  };

  const handleDelete = async (
    provisionalAttachmentId: number,
    fileName: string,
    fileDetails: any
  ) => {
    try {
      setLoadingNew(true);
      setShowDeleteModal(false);
      const resultUpdate = await deleteUploadRecordFile(
        provisionalAttachmentId,
        fileName
      );
      if (resultUpdate?.isSuccess) {
        setSnackbarMessage("Record has been deleted");
        setError("success");
        setSnackbarOpen(true);
        setFileToDelete(null);
        setIsSaveBtnDisable(true);
      }
      const updatedFiles = files.filter(
        (file) => file.id !== provisionalAttachmentId
      );
      const matchedFile = files.find(
        (file) => file.id === provisionalAttachmentId
      );

      const matchedRecord =
        selectedRowData?.caseRecordHeaderId &&
        result?.find((record: any) =>
          record?.addressesJson?.some(
            (attachment: any) =>
              attachment.provisionalAttachmentID === provisionalAttachmentId
          )
        );

      if (matchedRecord?.parentCaseRecordDetailsId) {
        setParentCaseRecordDetailsId(matchedRecord.parentCaseRecordDetailsId);
      } else {
        setParentCaseRecordDetailsId(null);
      }
      const caseDetailsID = matchedRecord?.parentCaseRecordDetailsId
        ? matchedRecord?.parentCaseRecordDetailsId
        : matchedFile?.caseRecordDetailsID ?? null;

      const parentId = fileDetails?.fileVersionTargetId ?? null;

      let finalFiles = [...updatedFiles];

      if (parentId) {
        const parentRecord = result.find((rec: any) =>
          rec?.addressesJson?.some(
            (att: any) => att.provisionalAttachmentID === parentId
          )
        );

        if (parentRecord) {
          const parentAttachment = parentRecord.addressesJson.find(
            (att: any) => att.provisionalAttachmentID === parentId
          );

          if (parentAttachment) {
            const rollbackRow: FileData = {
              id: parentAttachment.provisionalAttachmentID,
              fileName: parentAttachment.fileName,
              description: parentAttachment.description ?? "",
              noofPages: parentAttachment.noofPages ?? 0,
              isEditing: false,
              parentId: parentRecord.parentCaseRecordDetailsId ?? null,
              caseRecordDetailsID: parentAttachment.caseRecordDetailsID ?? null,
              fileVersionTargetId: null,
            };

            const deletedIndex = files.findIndex(
              (f) => f.id === provisionalAttachmentId
            );

            if (deletedIndex === -1) {
              finalFiles.push(rollbackRow);
            } else {
              finalFiles.splice(deletedIndex, 0, rollbackRow);
            }
          }
        }
      }

      setFiles(finalFiles);
      addFileAction(provisionalAttachmentId, caseDetailsID, "DeleteFile");
      onDelete?.(provisionalAttachmentId.toString());
      updateFileIdsInParent(updatedFiles);
    } catch (error) {
      console.error("Error deleting file:", error);
    } finally {
      setLoadingNew(false);
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    id?: string
  ) => {
    const { name } = e.target;
    let value = e.target.value;
    if (value.startsWith(" ")) return;
    value = value.replace(/ {2,}/g, " ");
    if (value && !ALPHANUMERIC_REGEXDESC.test(value)) return;
    if (id) {
      setFiles(
        files.map((item) =>
          item.id === id ? { ...item, [name]: value } : item
        )
      );
    } else {
      setNewFile({ ...newFile, [name]: value });
    }
  };

  const handleNewVersionInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    id?: string
  ) => {
    const { name } = e.target;
    let value = e.target.value;
    if (value.startsWith(" ")) return;
    value = value.replace(/\s{2,}/g, " ");
    if (value && !ALPHANUMERIC_REGEXDESC.test(value)) return;
    setNewVersionFile({ ...newVersionFile, [name]: value });
  };

  const handleCancelNewFile = () => {
    setNewFile({
      id: "",
      files: [],
      fileName: "",
      description: "",
      isEditing: true,
    });
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleCancelButton = (id?: string) => {
    if (id) {
      setFileToCancel(id);
      setIsSaveBtnDisableForRow(false);
    } else {
      setFileToCancel("new");
      setIsSaveBtnDisableForRow(false);
    }
    setShowModalNew(true);
  };

  const handleCloseModalNew = () => {
    setShowModalNew(false);
  };

  const handleConfirmCancelButton = () => {
    if (fileToCancel === "new") {
      handleCancelNewFile();
    } else {
      handleCancelEdit(fileToCancel);
    }
    setShowModalNew(false);
  };

  const handleDeleteFile = (id: number, fileName: string, fileDetails: any) => {
    setFileToDelete({ id, fileName, fileDetails });
    const matchedRecord =
      selectedRowData?.caseRecordHeaderId &&
      result?.find((record: any) =>
        record?.addressesJson?.some(
          (attachment: any) => attachment.provisionalAttachmentID === id
        )
      );

    const caseDetailsID = matchedRecord?.parentCaseRecordDetailsId
      ? matchedRecord?.parentCaseRecordDetailsId
      : fileDetails?.caseRecordDetailsID;
    setShowDeleteModal(true);
  };

  const handleAddNewVersion = (id: number, fileName: string, file: any) => {
    setFileNameParent(file?.fileName);
    setFileVersionTargetId(id);
    setCaseRecordDetailsIDParent(file?.caseRecordDetailsID);

    //find the matching parentCaseRecordDetailsId from full case records
    const matchedRecord = result.find((record: any) =>
      record.addressesJson?.some(
        (attachment: any) => attachment.provisionalAttachmentID === file.id
      )
    );

    if (matchedRecord?.parentCaseRecordDetailsId) {
      setParentCaseRecordDetailsId(matchedRecord.parentCaseRecordDetailsId);
    } else {
      setParentCaseRecordDetailsId(null);
    }

    setShowNewVersionModal(true);
  };

  const handleCloseModalNewVersion = () => {
    setShowNewVersionModal(false);
    setNewVersionFile({
      id: "",
      fileName: "",
      description: "",
      isEditing: true,
    });
    if (fileNewVersionInputRef.current) {
      fileNewVersionInputRef.current.value = "";
    }
  };

  const handleCloseModalDelete = () => {
    setShowDeleteModal(false);
  };

  const handleCancelSingleUpload = (fileId: string) => {
    setUploadStatuses((prev) => {
      const newStatuses = { ...prev };
      const cancel = newStatuses[fileId]?.cancel;
      const fileName = newStatuses[fileId]?.fileName;
      if (cancel) cancel();
      if (fileName && newFile.files) {
        setNewFile((prev: any) => ({
          ...prev,
          files: prev.files.filter((f: any) => f.name !== fileName),
        }));

        // Clear the input if no files remain
        if (newFile.files?.length === 1 && fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      }
      delete newStatuses[fileId];
      const remainingUploads = Object.values(newStatuses).filter(
        (status) => status.isUploading
      );

      if (remainingUploads.length === 0) {
        setIsSaveBtnDisableForRow(false);
        dispatch(setIsUploadFile(false));
      }

      return newStatuses;
    });
  };

  const handleCancelALLUpload = () => {
    setIsSaveBtnDisableForRow(false);
    setUploadStatuses((prev) => {
      const newStatuses = { ...prev };
      Object.values(newStatuses).forEach((status) => {
        if (status.cancel) status.cancel();
      });

      dispatch(setIsCancelFiles(false));
      dispatch(setIsUploadFile(false));

      return {};
    });
  };

  const isFileAttachedToRecord = (
    fileId: number | string,
    recordList: any[]
  ): number | null => {
    const matchedRecord = recordList?.find((record: any) =>
      record?.addressesJson?.some(
        (attachment: any) =>
          Number(attachment.provisionalAttachmentID) === Number(fileId)
      )
    );

    return matchedRecord?.parentCaseRecordDetailsId ?? null;
  };

  const isAnyRowEditing = files.some((file) => file.isEditing);
  const finalDocumentDisable = finalDocument && files?.length > 0;

  useEffect(() => {
    if (isCancelFiles) {
      handleCancelALLUpload();
    }
  }, [isCancelFiles]);

  const uploadingCount = Object.values(uploadStatuses).filter(
    (status) => status.isUploading
  ).length;

  useEffect(() => {
    dispatch(setIsUploadFile(uploadingCount ? uploadingCount > 0 : false));
    setIsSaveBtnDisableForRow(uploadingCount ? uploadingCount > 0 : false);
  }, [uploadingCount]);

  const handleToggleExpand = async (rowId: string) => {
    setExpandedRows((prev) => {
      const updated = new Set(prev);
      if (updated.has(rowId)) {
        updated.delete(rowId);
      } else {
        updated.add(rowId);
      }
      return updated;
    });

    // Fetch only if not already loaded
    if (!rowChildren[rowId]) {
      try {
        const response = await getVersionList(rowId);
        const data = response?.result;
        setRowChildren((prev) => ({ ...prev, [rowId]: data }));
      } catch (error) {
        console.error("Failed to fetch child rows:", error);
      }
    }
  };

  function findParentIdByProvisionalAttachmentId(
    result: any,
    targetId: number
  ) {
    for (const record of result) {
      if (record.addressesJson) {
        for (const attachment of record.addressesJson) {
          if (attachment.provisionalAttachmentID === targetId) {
            return record.parentCaseRecordDetailsId;
          }
        }
      }
    }
    return null;
  }

  const handleDownloadAction = async (row: any, downloadFile: string) => {
    try {
      setShowMessage(true);
      const recordId =
        downloadFile === "Parent"
          ? row?.caseRecordDetailsID
          : row?.caseRecordDetailsId;
      await getDownloadFileForPopUpDetails(recordId, row?.fileName);
    } catch (error) {
      console.error("Error while downloading file:", error);
    } finally {
      setShowMessage(false);
    }
  };

  const [selectedPdfUrl, setSelectedPdfUrl] = useState<string | null>(null);
  const [isViewerOpen, setIsViewerOpen] = useState(false);

  const viewPDF = (row: any) => {
    const fileUrl = row;

    const isPdf = fileUrl?.toLowerCase().endsWith(".pdf");
    const isImage = /\.(png|jpe?g|gif|tiff?|heif|heic|webp)$/i.test(
      fileUrl || ""
    );

    if (isPdf || isImage) {
      setSelectedPdfUrl(fileUrl); // Or better name: setSelectedFileUrl
      setIsViewerOpen(true);
      onViwerOpen?.("Open");
    } else {
      alert("Unsupported file format");
    }
  };

  return (
    <>
      <div
        style={{ position: "fixed", top: "20px", right: "20px", zIndex: 1050 }}
      >
        {Object.values(uploadStatuses).some((status) => status.isUploading) && (
          <UploadStatusFloatingIcon
            uploadStatuses={uploadStatuses}
            onCancelUpload={handleCancelSingleUpload}
            isUpload={true}
            recordActiveTab={recordActiveTab}
            activeTab={activeTab}
            isAllCancel={isAllCancel}
          />
        )}
      </div>
      <Snackbar
        message={snackbarMessage}
        open={snackbarOpen}
        onClose={() => setSnackbarOpen(false)}
        severity={error}
      />
      <div className="table-container picklist picklist-record">
        <Table className="table  vieweditdatatable attachmentsection" style={{tableLayout:'fixed'}}>
          <thead>
            <tr>
              <th style={{ width: files?.length ? "73%" : "100%" }}>File Name</th>
              {/* <th style={{ width: "40%" }}>Description</th> */}
              {files?.length > 0 && (
                <>
                  <th style={{ width: "10%" }}>
                    Pages{" "}
                    {totalPagesCount && totalPagesCount > 0
                      ? `(${totalPagesCount})`
                      : ""}
                  </th>
                  <th style={{ width: "15%", textAlign: "left" }}>
                    Action
                  </th>
                </>
              )}
            </tr>
          </thead>

          <tbody>
            <tr>
              <td style={{ width: "25%" }}>
                <Form.Group>
                  <Form.Control
                    type="file"
                    ref={fileInputRef}
                    multiple={!finalDocument}
                    onChange={handleFileChange}
                    style={{ display: "none" }}
                  />
                  <div
                    className="file-drop-zone"
                    onClick={() => {
                      if (!isAnyRowEditing && !existsDocType && !finalDocumentDisable)
                        fileInputRef.current?.click();
                    }}
                    onMouseEnter={(e) => {
                      if (!isAnyRowEditing && !existsDocType && !finalDocumentDisable)
                        e.currentTarget.classList.add("drag-over");
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.classList.remove("drag-over");
                    }}
                    onDragOver={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (!isAnyRowEditing && !existsDocType && !finalDocumentDisable)
                        e.currentTarget.classList.add("drag-over");
                    }}
                    onDragLeave={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      e.currentTarget.classList.remove("drag-over");
                    }}
                    onDrop={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      e.currentTarget.classList.remove("drag-over");
                      if (!isAnyRowEditing && !existsDocType && !finalDocumentDisable) {
                        const files = Array.from(e.dataTransfer.files);
                        if (finalDocument) {
                          if (files?.length > 1) {
                            setSnackbarMessage("Only one file allowed for final document");
                            setError("error");
                            setSnackbarOpen(true);
                            return;
                          }
                        }
                        handleFileChange({ target: { files } } as any);
                      }
                    }}
                    style={{
                      cursor:
                        isAnyRowEditing || existsDocType || finalDocumentDisable
                          ? "not-allowed"
                          : "pointer",
                    }}
                  >
                    <InputGroup>
                      <Form.Control
                        type="text"
                        placeholder="Choose a file or drag it here"
                        value={newFile.files.map((f: any) => f.name).join(", ")}
                        readOnly
                        className="border-end-0"
                        required={true}
                        disabled={isAnyRowEditing || existsDocType || finalDocumentDisable}
                        style={{ cursor: "pointer", minHeight: "55px" }}
                      />
                      <Button
                        variant="outline-secondary"
                        className="p-0 border-start-0 attachbtn d-flex align-items-center"
                        onClick={(e) => e.preventDefault()}
                        disabled={isAnyRowEditing || existsDocType || finalDocumentDisable}
                      >
                        <span className="material-symbols-outlined color_brand mx-1">
                          attach_file
                        </span>
                      </Button>
                    </InputGroup>
                  </div>
                </Form.Group>
              </td>
              {/* <td className="uploadrecordtext" style={{ width: "50%" }}>
                <Form.Control
                  as="textarea"
                  name="description"
                  style={{
                    padding: "5px",
                    borderRadius: "4px",
                    border: "1px solid #ccc",
                    minHeight: "55px",
                    maxHeight: "120px",
                    fontSize: "13px",
                  }}
                  value={newFile.description}
                  onChange={handleInputChange}
                  placeholder="Enter description"
                  disabled={isAnyRowEditing || existsDocType}
                  maxLength={300}
                />
              </td>
              <td style={{ width: "10%", textAlign: "center" }}>-</td>
              <td style={{ width: "250px" }}>
                <div
                  style={{
                    display: "flex",
                    margin: "1px",
                    justifyContent: "start",
                  }}
                >
                  <Button
                    className="btn btn-primary d-flex px-2 p-1 align-items-center justify-content-center"
                    onClick={handleAddFile}
                    disabled={
                      !newFile.files.length || isAnyRowEditing || existsDocType
                    }
                  >
                    <span className="material-symbols-outlined me-1 d-flex align-items-center ">
                      add_circle
                    </span>{" "}
                    Add
                  </Button>
                  <Button
                    variant="danger"
                    className="border-0 bg-transparent m-1 px-1 d-flex align-items-center"
                    onClick={() => handleCancelButton()}
                    disabled={isAnyRowEditing || existsDocType}
                  >
                    <span className="material-icons color_brand mx-1">
                      close
                    </span>
                  </Button>
                </div>
              </td> */}
            </tr>
            {showMessage && (
              <div className="alert alert-info text-center position-fixed">
                Download is in progress...
              </div>
            )}

            {files?.map((file, index) => {
              const rowId = Array.isArray(result)
                ? findParentIdByProvisionalAttachmentId(result, file.id)
                : null;
              const rowIdNew = Array.isArray(result)
                ? findParentIdByProvisionalAttachmentId(
                    result,
                    file.fileVersionTargetId
                  )
                : null;
              const finalRowId = rowId || rowIdNew;
              const isClickable = Boolean(finalRowId);
              const isExpanded = expandedRows.has(finalRowId);
              const iconStyle = {
                cursor: isClickable ? "pointer" : "not-allowed",
                color: isClickable ? "#0098d7" : "#ccc",
                fontSize: 20,
                marginRight: 8,
              };

              const displayName =
                file.fileName && file.fileName.length > 15
                  ? truncateMiddleString(file.fileName)
                  : file.fileName;

              return (
                <React.Fragment key={file.id}>
                  <tr key={file.id}>
                    <td style={{ width: "20%" }}>
                      <div className="d-flex align-items-center">
                        <span
                          className="material-symbols-outlined"
                          style={iconStyle}
                          onClick={
                            isClickable
                              ? () => handleToggleExpand(finalRowId)
                              : undefined
                          }
                        >
                          {isExpanded ? "do_not_disturb_on" : "add_circle"}
                        </span>

                        {file.fileName && (
                          <MiddleTruncatedTextWithTooltip
                            text={file.fileName}
                            tooltipId={`tooltip-fileName-${file.id}`}
                          />
                        )}
                      </div>
                    </td>
                    {/* <td className="uploadrecordtext" style={{ width: "50%" }}>
                      {file.isEditing ? (
                        <Form.Control
                          as="textarea"
                          name="description"
                          style={{
                            padding: "5px",
                            borderRadius: "4px",
                            border: "1px solid #ccc",
                            minHeight: "55px",
                            maxHeight: "120px",
                            fontSize: "13px",
                          }}
                          value={file.description}
                          onChange={(e) => handleInputChange(e, file.id)}
                          maxLength={300}
                        />
                      ) : file.description && file.description?.length > 15 ? (
                        <OverlayTrigger
                          placement="auto"
                          delay={{ show: 200, hide: 10 }}
                          overlay={
                            <Tooltip id={`tooltip-description-${file.id}`}>
                              {file.description}
                            </Tooltip>
                          }
                        >
                          <span
                            className="d-inline-block text-truncate"
                            style={{
                              maxWidth: "525px",
                              cursor: "default",
                              marginLeft:'5px', 
                              display: 'inline-block',
                            }}
                          >
                            {file.description}
                          </span>
                        </OverlayTrigger>
                      ) : (
                        <span style={{ cursor: "default", marginLeft:'5px', display: 'inline-block' }}>
                          {file.description}
                        </span>
                      )}
                    </td> */}
                    <td style={{ width: "10%", textAlign: "center" }}>
                      {file.noofPages === null ? (
                        <span style={{ fontSize: "12px", color: "#666" }}>Calculating...</span>
                      ) : file.noofPages && file.noofPages > 0 ? (
                        file.noofPages
                      ) : (
                        "-"
                      )}
                    </td>
                    <td style={{ width: "200px" }}>
                      <div
                        style={{
                          display: "flex",
                          margin: "1px",
                          justifyContent: "start",
                        }}
                      >
                        {file.isEditing ? (
                          <>
                            <Button
                              variant="success"
                              className="border-0 bg-transparent px-1"
                              onClick={() =>
                                handleSaveEdit(
                                  file.id,
                                  file,
                                  hasVersions(file.id),
                                  !!file.parentId
                                )
                              }
                            >
                              <span className="material-icons color_brand mx-1">
                                save
                              </span>
                            </Button>
                            <Button
                              variant="danger"
                              className="border-0 bg-transparent p-0"
                              onClick={() => handleCancelButton(file.id)}
                            >
                              <span className="material-icons color_brand mx-1">
                                close
                              </span>
                            </Button>
                          </>
                        ) : (
                          <>
                            {file?.fileName &&
                              /\.(pdf|png|jpe?g|gif|tiff?|heif|heic|webp)$/i.test(
                                file.fileName
                              ) && (
                                <Button
                                  variant="success"
                                  className="border-0 bg-transparent m-1 p-0"
                                  style={{ cursor: "pointer" }}
                                  onClick={() =>
                                    viewPDF?.(file.cloudStoragePath)
                                  }
                                >
                                  <span className="material-icons material-symbols-outlined color_brand mt-1 mx-1">
                                    document_search
                                  </span>
                                </Button>
                              )}
                            {/* <Button
                              variant="success"
                              className="border-0 bg-transparent m-1 p-0"
                              onClick={() => handleEdit(file.id)}
                              disabled={isAnyRowEditing || existsDocType}
                            >
                              <span className="material-icons color_brand m-1">
                                {" "}
                                edit
                              </span>
                            </Button> */}
                            <Button
                              variant="danger"
                              className="border-0 bg-transparent m-1 p-0"
                              onClick={() =>
                                handleDeleteFile(file.id, file.fileName, file)
                              }
                              disabled={isAnyRowEditing || existsDocType}
                            >
                              <span className="material-icons text-danger material-symbols-outlined color_brand m-1">
                                delete
                              </span>
                            </Button>
                            {file?.caseRecordDetailsID ? (
                              <Button
                                variant="success"
                                className="border-0 bg-transparent m-1 p-0"
                                style={{ cursor: "pointer" }}
                                onClick={() =>
                                  handleDownloadAction?.(file, "Parent")
                                }
                                disabled={
                                  !file?.caseRecordDetailsID || isAnyRowEditing
                                }
                              >
                                <span className="material-icons color_brand mt-1 mx-1">
                                  download
                                </span>
                              </Button>
                            ) : (
                              ""
                            )}

                            {isVersionUploadAllowed(file.fileName) &&
                            file?.caseRecordDetailsID ? (
                              <OverlayTrigger
                                key={tooltipKey}
                                placement="auto"
                                delay={{ show: 200, hide: 10 }}
                                overlay={
                                  <Tooltip id={`tooltip-upload-${file.id}`}>
                                    Upload New Version
                                  </Tooltip>
                                }
                              >
                                <span className="d-inline-block">
                                  <Button
                                    variant="outlined"
                                    className="border-0 bg-transparent m-1 p-0 btn-text-border"
                                    onClick={() => {
                                      handleAddNewVersion(
                                        file.id,
                                        file.fileName,
                                        file
                                      );
                                      setTooltipKey((prev) => prev + 1);
                                    }}
                                    disabled={isAnyRowEditing || existsDocType}
                                  >
                                    <span className="material-icons color_brand m-1">
                                      upload
                                    </span>
                                  </Button>
                                </span>
                              </OverlayTrigger>
                            ) : (
                              ""
                            )}
                          </>
                        )}
                      </div>
                    </td>
                  </tr>

                  {isExpanded &&
                    rowChildren[finalRowId]?.map((child: any) => (
                      <tr
                        key={child.caseRecordDetailsId}
                        style={{ backgroundColor: "#eef8fb" }}
                      >
                        {VERSION_HEADER_EDIT.map((header: any) => {
                          if (header.key === "revisionNoDesc") {
                            return (
                              <td key={header.key}>
                                {child.revisionNoDesc && (
                                  <span
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "start",
                                      gap: "4px",
                                      marginLeft: "35px",
                                    }}
                                  >
                                    <span
                                      className="material-symbols-outlined"
                                      style={{
                                        fontSize: "18px",
                                        cursor: "default",
                                        color:
                                          child.isLatestVersion == 1
                                            ? "#50dd32"
                                            : "#0098d7",
                                      }}
                                    >
                                      {child.isLatestVersion == 1
                                        ? "charger"
                                        : "history"}
                                    </span>
                                    {child[header.key]}&nbsp;&nbsp;
                                    {child.fileName &&
                                    <MiddleTruncatedTextWithTooltip
                                      text={child.fileName}
                                      tooltipId={`tooltip-child-fileName-${child.caseRecordDetailsId}`}
                                    />}
                                  </span>
                                )}
                              </td>
                            );
                          }

                          {header.key === "noOfPages" && (
                            <td
                              key={header.key}
                              style={{ width: "10%", textAlign: "center" }}
                            >
                              {child.noOfPages === null ? (
                                <span style={{ fontSize: "12px", color: "#666" }}>Calculating...</span>
                              ) : child.noOfPages === 0 ? (
                                "-"
                              ) : (
                                child.noOfPages
                              )}
                            </td>
                          )}

                          if (header.key === "download") {
                            return (
                              <td
                                key={header.key}
                                style={{
                                  padding: "8px",
                                  textAlign: "left",
                                }}
                              >
                                {child?.fileName &&
                                  /\.(pdf|png|jpe?g|gif|tiff?|heif|heic|webp)$/i.test(
                                    child.fileName
                                  ) && (
                                    <span
                                      className="material-icons material-symbols-outlined color_brand"
                                      onClick={() =>
                                        viewPDF?.(child?.cloudLocation)
                                      }
                                      style={{ cursor: "pointer" }}
                                      title="View File"
                                    >
                                      document_search
                                    </span>
                                  )}
                                {child.download && (
                                  <span
                                    className="material-icons color_brand"
                                    onClick={() =>
                                      handleDownloadAction?.(child, "Child")
                                    }
                                    style={{
                                      cursor: "pointer",
                                      marginRight: "8px",
                                    }}
                                    title="Download"
                                  >
                                    download
                                  </span>
                                )}
                              </td>
                            );
                          }

                          return (
                            <td key={header.key}>
                              <OverlayTrigger
                                placement="auto"
                                delay={{ show: 200, hide: 10 }}
                                overlay={
                                  child[header.key] &&
                                  child[header.key].length > 15 ? (
                                    <Tooltip
                                      id={`tooltip-${header.key}-${child.id}`}
                                    >
                                      {child[header.key]}
                                    </Tooltip>
                                  ) : (
                                    <></>
                                  )
                                }
                              >
                                <span
                                  className="d-inline-block text-truncate"
                                  style={{
                                    maxWidth: "400px",
                                    cursor: "default",
                                    marginLeft:'5px',
                                    display: 'inline-block'
                                  }}
                                >
                                  {child[header.key]}
                                </span>
                              </OverlayTrigger>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                </React.Fragment>
              );
            })}
          </tbody>
        </Table>
      </div>
      <Modal show={showModalNew} onHide={handleCloseModalNew}>
        <Modal.Body className="fw-bold text-left modelheading ">
          <div>Are you sure you want to cancel?</div>
        </Modal.Body>
        <Modal.Footer className="border-top d-flex justify-content-end">
          <Button
            variant="outline-info"
            className="d-flex align-items-end"
            onClick={handleCloseModalNew}
          >
            <span className="material-icons mr-2">close</span> Cancel
          </Button>
          <Button
            onClick={handleConfirmCancelButton}
            variant="primary"
            className="ms-3 d-flex align-items-center"
          >
            <span className="material-icons mx-1">check</span> Confirm
          </Button>
        </Modal.Footer>
      </Modal>
      <Modal show={showDeleteModal} onHide={handleCloseModalDelete}>
        <Modal.Body className="fw-bold text-left modelheading ">
          <div>Are you sure you want to delete this record?</div>
        </Modal.Body>
        <Modal.Footer className="border-top d-flex justify-content-end">
          <Button
            variant="outline-info"
            className="d-flex align-items-end"
            onClick={handleCloseModalDelete}
          >
            <span className="material-icons mr-2">close</span> Cancel
          </Button>
          <Button
            onClick={() =>
              fileToDelete &&
              handleDelete(
                fileToDelete.id,
                fileToDelete.fileName,
                fileToDelete.fileDetails
              )
            }
            variant="primary"
            className="ms-3 d-flex align-items-center"
          >
            <span className="material-icons mx-1">check</span> Confirm
          </Button>
        </Modal.Footer>
      </Modal>
      <Modal
        show={showNewVersionModal}
        onHide={handleCloseModalNewVersion}
        aria-labelledby="contained-modal-title-vcenter"
        centered
        className="recordList"
      >
        <Modal.Header closeButton className="d-flex">
          <Modal.Title className="fw-bold text-left modelheading offcanvas-title ">
            Add Version
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="fw-bold text-left modelheading ">
          <Form.Group>
            <Form.Control
              type="file"
              ref={fileNewVersionInputRef}
              onChange={handleNewVersionFileChange}
              style={{ display: "none" }}
              multiple={false}
            />
            <div
              className="mb-3 file-drop-zoneNewversion"
              onClick={() => {
                if (!isAnyRowEditing) fileNewVersionInputRef.current?.click();
              }}
              onMouseEnter={(e) => {
                if (!isAnyRowEditing)
                  e.currentTarget.classList.add("drag-over");
              }}
              onMouseLeave={(e) => {
                e.currentTarget.classList.remove("drag-over");
              }}
              onDragOver={(e) => {
                e.preventDefault();
                e.stopPropagation();
                e.currentTarget.classList.add("drag-over");
                if (!isAnyRowEditing) {
                  const file = e.dataTransfer.files[0];
                  if (file) {
                    handleNewVersionFileChange({
                      target: { files: [file] },
                    } as any);
                  }
                }
              }}
              onDragLeave={(e) => {
                e.preventDefault();
                e.stopPropagation();
                e.currentTarget.classList.remove("drag-over");
              }}
              onDrop={(e) => {
                e.preventDefault();
                e.stopPropagation();
                e.currentTarget.classList.remove("drag-over");
                if (!isAnyRowEditing) {
                  const files = Array.from(e.dataTransfer.files);
                  handleNewVersionFileChange({ target: { files } } as any);
                }
              }}
              style={{
                cursor: isAnyRowEditing ? "not-allowed" : "pointer",
              }}
            >
              <InputGroup>
                <Form.Control
                  type="text"
                  placeholder="Choose a file or drag it here"
                  value={newVersionFile.fileName}
                  readOnly
                  className="border-end-0"
                  required={true}
                  disabled={isAnyRowEditing}
                  style={{
                    cursor: "pointer",
                    minHeight: "50px",
                    fontSize: "14px",
                  }}
                />
                <Button
                  variant="outline-secondary"
                  className="p-0 border-start-0 attachbtn d-flex align-items-center"
                  onClick={(e) => e.preventDefault()}
                  disabled={isAnyRowEditing}
                >
                  <span className="material-symbols-outlined color_brand mx-1">
                    attach_file
                  </span>
                </Button>
              </InputGroup>
            </div>
          </Form.Group>
          <Form.Control
            as="textarea"
            name="description"
            style={{
              padding: "5px",
              borderRadius: "4px",
              border: "1px solid #ccc",
              minHeight: "100px",
              fontSize: "13px",
            }}
            value={newVersionFile.description}
            onChange={handleNewVersionInputChange}
            placeholder="Enter description"
            disabled={isAnyRowEditing}
            maxLength={300}
          />
        </Modal.Body>
        <Modal.Footer className="d-flex justify-content-end">
          <Button
            variant="outline-info"
            className="d-flex align-items-end align-items-center"
            onClick={handleCloseModalNewVersion}
          >
            <span className="material-icons mr-2">close</span> Cancel
          </Button>
          <Button
            onClick={handleAddNewVersionFile}
            variant="primary"
            className="ms-3 d-flex align-items-center"
          >
            <span className="material-icons mx-1">save</span> Save
          </Button>
        </Modal.Footer>
      </Modal>
      <PdfViewerDrawer
        isOpen={isViewerOpen}
        onClose={() => {
          setIsViewerOpen(false);
          onViwerOpen?.("");
        }}
        fileUrl={selectedPdfUrl}
      />
    </>
  );
};

export default HistorianRecordEditableTable;
